import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { contactsApi, teamApi, projectsApi, entitiesApi } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { Plus, X, Search, Send } from "lucide-react";
import { toast } from "sonner";

/**
 * Shared Contact → Team promotion / invite dialog.
 *
 * Modes:
 *   - preselectContact: a canonical Contact is already chosen (used by
 *     Contact profile "Add to Team"). The search step is skipped and
 *     the identity fields are locked. If the contact has no email the
 *     admin is prompted for one before send.
 *   - search mode (default): step 1 = search existing Contacts by
 *     name / email / phone. If one is picked and already has a linked
 *     user, show "Already Team Member → open profile" and block
 *     re-invite. Otherwise prefill from the chosen Contact. If no match
 *     is appropriate, admin picks "Create new person" and enters the
 *     identity manually (backend auto-creates the canonical Contact via
 *     ensure_user_contact on accept).
 *
 * Multi-project assignments: admin can attach the invitee to 0..N
 * projects with per-project role + access level in a single pass.
 */
const PROJECT_ROLES = [
  "discovery_agent", "researcher", "research_coordinator",
  "business_development", "community_engagement", "pro_network_advisor",
  "project_coordinator", "project_manager", "producer", "co_producer",
  "host", "co_host", "content_communications", "operations",
  "advisor", "contributor", "observer",
];
const ACCESS_LEVELS = [
  { value: "view", label: "View" },
  { value: "contribute", label: "Contribute" },
  { value: "manage_project", label: "Manage Project" },
];
const roleLabel = (slug) => (slug || "").split("_").map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(" ");

export default function TeamInviteDialog({ open, onClose, preselectContact = null, onSent }) {
  const [mode, setMode] = useState("search"); // search | prefilled | new
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [chosenContact, setChosenContact] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", platform_role: "member",
  });
  const [assignments, setAssignments] = useState([]); // [{project_id, project_role, access_level}]
  const [projects, setProjects] = useState([]);
  const [entities, setEntities] = useState([]);
  const [sending, setSending] = useState(false);

  // Load projects + entities once per open. Reset state on open/close.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [p, e] = await Promise.all([projectsApi.list(), entitiesApi.list()]);
        setProjects(p.data || []); setEntities(e.data || []);
      } catch {}
    })();
    if (preselectContact) {
      setChosenContact(preselectContact);
      setForm({
        name: preselectContact.full_name || "",
        email: preselectContact.email || "",
        phone: preselectContact.phone || "",
        platform_role: "member",
      });
      setMode("prefilled");
    } else {
      setMode("search");
      setChosenContact(null);
      setForm({ name: "", email: "", phone: "", platform_role: "member" });
      setQuery("");
      setResults([]);
    }
    setAssignments([]);
  }, [open, preselectContact]);

  // Debounced contact search by name / email / phone.
  useEffect(() => {
    if (!open || mode !== "search") return;
    const q = (query || "").trim();
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    const h = setTimeout(async () => {
      try {
        const r = await contactsApi.list({ q });
        setResults(r.data || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 220);
    return () => clearTimeout(h);
  }, [query, open, mode]);

  const projectsById = useMemo(() => Object.fromEntries(projects.map(p => [p.id, p])), [projects]);
  const entitiesById = useMemo(() => Object.fromEntries(entities.map(e => [e.id, e])), [entities]);

  const pickContact = (c) => {
    if (c.linked_user_id) {
      // Already a team member — block re-invite, let admin jump to profile.
      setChosenContact({ ...c, _is_team: true });
      return;
    }
    setChosenContact(c);
    setForm({
      name: c.full_name || "",
      email: c.email || "",
      phone: c.phone || "",
      platform_role: "member",
    });
    setMode("prefilled");
  };

  const addAssignment = () =>
    setAssignments([...assignments, { project_id: "", project_role: "contributor", access_level: "contribute" }]);
  const updateAssignment = (i, patch) => {
    const next = assignments.slice();
    next[i] = { ...next[i], ...patch };
    setAssignments(next);
  };
  const removeAssignment = (i) =>
    setAssignments(assignments.filter((_, idx) => idx !== i));

  const submit = async () => {
    const email = (form.email || "").trim().toLowerCase();
    if (!email) {
      toast.error("Email is required to send an invitation");
      return;
    }
    const bad = assignments.find(a => !a.project_id);
    if (bad) {
      toast.error("Pick a project for every assignment (or remove it)");
      return;
    }
    setSending(true);
    try {
      const payload = {
        email,
        name: form.name || "",
        phone: form.phone || "",
        platform_role: form.platform_role,
        contact_id: chosenContact?.id || undefined,
        project_assignments: assignments.map(a => ({
          project_id: a.project_id,
          project_role: a.project_role || "contributor",
          access_level: a.access_level || "contribute",
        })),
      };
      await teamApi.invite(payload);
      toast.success("Invitation sent");
      onSent && onSent();
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invitation failed");
    } finally { setSending(false); }
  };

  const takenProjectIds = new Set(assignments.map(a => a.project_id).filter(Boolean));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-xl bg-[#151A23] border-white/10 text-white" data-testid="team-invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            {mode === "search" ? "Invite to HQ" : preselectContact ? "Add to Team" : "Invite to HQ"}
          </DialogTitle>
          <p className="text-xs text-white/50 mt-1">
            {mode === "search"
              ? "Search existing Contacts first — one person, one record."
              : chosenContact
                ? "Prefilled from the canonical Contact. Only internal setup below."
                : "Enter the person once — we'll create their canonical Contact on accept."}
          </p>
        </DialogHeader>

        {/* --- STEP 1: SEARCH --- */}
        {mode === "search" && (
          <div className="space-y-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, email, or phone…"
                className="pl-9 bg-white/5 border-white/10 text-sm"
                data-testid="invite-contact-search" autoFocus />
            </div>
            {searching && <div className="text-xs text-white/40">Searching…</div>}
            {!searching && query.trim().length >= 2 && results.length === 0 && (
              <div className="text-xs text-white/50 italic">No matching contacts.</div>
            )}
            <div className="max-h-72 overflow-auto space-y-1">
              {results.slice(0, 25).map(c => (
                <button key={c.id}
                        onClick={() => pickContact(c)}
                        className="w-full text-left p-2.5 rounded border border-white/10 hover:bg-white/5 flex items-center justify-between gap-2"
                        data-testid={`invite-contact-pick-${c.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">{c.full_name}</div>
                    <div className="text-[11px] text-white/50 truncate">
                      {[c.email, c.phone, c.role, c.company].filter(Boolean).join(" · ") || "No contact details"}
                    </div>
                  </div>
                  {c.linked_user_id && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
                      Team Member
                    </span>
                  )}
                </button>
              ))}
            </div>

            {chosenContact?._is_team && (
              <div className="p-3 rounded border border-emerald-500/30 bg-emerald-500/10 text-sm" data-testid="invite-contact-already-team">
                <div className="text-emerald-200">{chosenContact.full_name} is already a Team Member.</div>
                <Link to={`/member/${chosenContact.linked_user_id}`}
                      onClick={() => onClose && onClose()}
                      className="text-xs text-emerald-200 underline mt-1 inline-block">
                  Open member profile →
                </Link>
              </div>
            )}

            <div className="flex items-center justify-between pt-1 border-t border-white/5">
              <div className="text-[11px] text-white/40">Not in Contacts yet?</div>
              <Button variant="outline" size="sm"
                      onClick={() => { setMode("new"); setChosenContact(null); setForm({ name: "", email: "", phone: "", platform_role: "member" }); }}
                      data-testid="invite-create-new-person"
                      className="rounded-full">
                Create new person
              </Button>
            </div>
          </div>
        )}

        {/* --- STEP 2: PREFILLED / NEW IDENTITY + ASSIGNMENTS --- */}
        {(mode === "prefilled" || mode === "new") && (
          <div className="space-y-4">
            {chosenContact && !chosenContact._is_team && (
              <div className="p-3 rounded border border-white/10 bg-white/[0.03] flex items-center justify-between gap-2" data-testid="invite-selected-contact">
                <div>
                  <div className="text-sm text-white">{chosenContact.full_name}</div>
                  <div className="text-[11px] text-white/50">
                    Prefilled from canonical Contact — no duplicate will be created.
                  </div>
                </div>
                {!preselectContact && (
                  <Button variant="ghost" size="sm" onClick={() => { setMode("search"); setChosenContact(null); }}
                          className="text-white/60">Change</Button>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-white/40">Full name</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                       readOnly={!!chosenContact} className="mt-1.5 bg-white/5 border-white/10"
                       data-testid="invite-form-name" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-white/40">
                  Email {chosenContact && !chosenContact.email && <span className="text-amber-300">(required to send)</span>}
                </Label>
                <Input type="email" value={form.email}
                       onChange={e => setForm({ ...form, email: e.target.value })}
                       readOnly={!!chosenContact && !!chosenContact.email}
                       className="mt-1.5 bg-white/5 border-white/10"
                       data-testid="invite-form-email" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-white/40">Phone</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                       readOnly={!!chosenContact && !!chosenContact.phone}
                       className="mt-1.5 bg-white/5 border-white/10"
                       data-testid="invite-form-phone" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-white/40">Team role</Label>
                <Select value={form.platform_role} onValueChange={v => setForm({ ...form, platform_role: v })}>
                  <SelectTrigger className="mt-1.5 bg-white/5 border-white/10" data-testid="invite-form-platform-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#151A23] border-white/10 text-white">
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Project assignments */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-[11px] uppercase tracking-wider text-white/40">Project assignments</Label>
                <Button variant="ghost" size="sm" onClick={addAssignment} className="text-white/80 h-7" data-testid="invite-add-assignment">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add project
                </Button>
              </div>
              {assignments.length === 0 && (
                <div className="text-[11px] text-white/40 italic">No projects assigned — the member will join HQ without project access yet.</div>
              )}
              <div className="space-y-2">
                {assignments.map((a, i) => (
                  <div key={i} className="p-2.5 rounded border border-white/10 bg-white/[0.02] space-y-2" data-testid={`invite-assignment-${i}`}>
                    <div className="flex items-start justify-between gap-2">
                      <Select value={a.project_id || "__none"} onValueChange={v => updateAssignment(i, { project_id: v === "__none" ? "" : v })}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-sm flex-1" data-testid={`invite-assignment-project-${i}`}>
                          <SelectValue placeholder="Choose project" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#151A23] border-white/10 text-white max-h-80">
                          <SelectItem value="__none">— select —</SelectItem>
                          {entities.map(e => {
                            const projs = projects.filter(p => p.entity_id === e.id && (!takenProjectIds.has(p.id) || p.id === a.project_id));
                            if (projs.length === 0) return null;
                            return (
                              <SelectGroup key={e.id}>
                                <SelectLabel className="text-white/50">{e.name}</SelectLabel>
                                {projs.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                              </SelectGroup>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="sm" onClick={() => removeAssignment(i)}
                              className="text-red-400 h-9" data-testid={`invite-assignment-remove-${i}`}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Select value={a.project_role} onValueChange={v => updateAssignment(i, { project_role: v })}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-xs" data-testid={`invite-assignment-role-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#151A23] border-white/10 text-white max-h-80">
                          {PROJECT_ROLES.map(r => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Select value={a.access_level} onValueChange={v => updateAssignment(i, { access_level: v })}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-xs" data-testid={`invite-assignment-access-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#151A23] border-white/10 text-white">
                          {ACCESS_LEVELS.map(al => <SelectItem key={al.value} value={al.value}>{al.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="text-[11px] text-white/40">
                      {a.project_id && projectsById[a.project_id]
                        ? `${entitiesById[projectsById[a.project_id].entity_id]?.name || "—"} → ${projectsById[a.project_id].name}`
                        : "Project not yet selected"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onClose && onClose()} className="text-white/70">Cancel</Button>
          {(mode === "prefilled" || mode === "new") && (
            <Button onClick={submit} disabled={sending}
                    className="rounded-full bg-white text-black hover:bg-white/90"
                    data-testid="invite-send-btn">
              <Send className="w-4 h-4 mr-1.5" />
              {sending ? "Sending…" : "Send invitation"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
